import {
  Classes,
  FormGroup,
  FormGroupProps,
  Icon,
  IconSize,
  InputGroup,
  InputGroupProps,
  MaybeElement,
} from "@blueprintjs/core"
import currencySymbol from "locale/currency"
import React from "react"
import classNames from "utils/classNames"
import { CustomIconName } from "utils/custom.types"
import { withFormController } from "../Molecules/FormController"
// Component Name - String Input
// Component Used For - String Inputs and Numbers
// Author - Malik Bagwala

/*
Props
1. className - a unique classname to add to the StringInput
2. label - label for the string input
3. helperText - used for displaying size info or errors
4. placeholder - used for displaying temporary text
5. big - increases the size of the StringInput
6. fill - complety fill the entire container
7. labelProps - object containing classes to allow modification
  eg. labelProps={{
      'vt-label-italic': true,
      'vt-label-bold': true
      }}
8. error - this is the custom error message which is displayed on the input a few features like
   helperText and intent change to danger whenever there is an error
9. valueAs - options ( 'int' or 'float') returns the respective type of  data when type property is     set as a number

Blueprint
Component - InputGroup, FormGroup

Link to Documentation -
https://blueprintjs.com/docs/#core/components/text-inputs.input-group
https://blueprintjs.com/docs/#core/components/form-group
*/
interface StringInputProps
  extends Omit<InputGroupProps, "leftIcon">,
    FormGroupProps {
  big?: boolean
  labelProps?: object
  error?: string
  onAdd: () => {}
  onRemove: () => {}
  valueAs: "float" | "string" | undefined
  required?: boolean
  protectedInput?: boolean
  path?: string | Array<string> | undefined
  maxLength?: number
  value?: string | undefined
  onChange?: any
  leftIcon?: CustomIconName | MaybeElement
}
const StringInput = ({
  label,
  big,
  helperText,
  labelInfo,
  labelProps,
  className,
  onChange,
  style,
  round,
  inline,
  intent,
  leftIcon,
  fill,
  error,
  onAdd,
  onRemove,
  value,
  rightElement,
  valueAs,
  required,
  maxLength,
  protectedInput,
  path,
  type,
  ...otherProps
}: StringInputProps) => {
  function parseInput(
    value: string,
    type: string | undefined,
    valueAs: "float" | "string" | undefined
  ) {
    switch (type) {
      case "number":
        switch (valueAs) {
          case "float":
            return parseFloat(value) || parseFloat(value) === 0
              ? parseFloat(value)
              : null
          case "string":
            return value
          default:
            return parseInt(value) || parseInt(value) === 0
              ? parseInt(value)
              : null
        }
      default:
        return value
    }
  }
  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = parseInput(e?.target?.value, type, valueAs)
    onChange && onChange(path, newValue)
  }

  // render() {

  const showError =
    typeof value === "string"
      ? error && value.length > 0
      : error && value && value > 0
  return (
    <FormGroup
      style={style}
      className={classNames(
        "vt-custom-text-input",
        labelProps,
        {
          "vt-input-big": big,
          "vt-input-rounded": round,
          "vt-input-stretch": fill,
          "vt-input-has-right-element": rightElement,
        },
        className
      )}
      helperText={showError ? error : helperText}
      label={label}
      inline={inline}
      intent={showError ? "danger" : intent}
      labelInfo={
        required ? <span className={Classes.INTENT_DANGER}>*</span> : labelInfo
      }
    >
      <InputGroup
        round={round}
        type={type}
        intent={showError ? "danger" : intent}
        maxLength={maxLength}
        leftIcon={
          leftIcon === "rupee" ? (
            <span
              style={{
                position: "absolute",
                zIndex: 15,
                left: 10,
                top: 6,
              }}
            >
              {currencySymbol}
            </span>
          ) : (
            <Icon
              icon={leftIcon}
              intent={showError ? "danger" : intent}
              iconSize={big ? IconSize.LARGE : IconSize.STANDARD}
            ></Icon>
          )
        }
        value={value ?? ""}
        rightElement={rightElement}
        onChange={handleInput}
        {...otherProps}
      />
    </FormGroup>
  )
}

StringInput.defaultProps = {
  placeholder: "",
  labelInfo: "",
  value: "",
  type: "text",
  big: true,
  valueAs: "float",
}
export default withFormController(StringInput)
